import { useState } from 'react';
import { Rocket, Diamond, Sparkles } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { toast, Toaster } from 'sonner';
import Logo from '../../assets/Logo.png';

// Features data
const features = [
    {
        title: "Project Management",
        description: "Track and manage projects from onboard to final payment with ease.",
        tag: "Core"
    },
    {
        title: "Client Portal",
        description: "Give clients a dedicated space to view progress and collaborate.",
        tag: "Collaboration"
    },
    {
        title: "Lead Tracking",
        description: "Never miss a potential client with organized lead management.",
        tag: "Sales"
    },
    {
        title: "Contract E-signing",
        description: "Create and sign professional contracts with built-in e-signatures.",
        tag: "Legal"
    },
    {
        title: "Task Tracking",
        description: "Organize work with intuitive Kanban boards and task management.",
        tag: "Productivity"
    },
    {
        title: "Payments",
        description: "Record payments - Milestone or Fixed based.",
        tag: "Finance"
    },
    {
        title: "Real-time Chat",
        description: "Communicate with clients efficiently in organized project chats.",
        tag: "Communication"
    },
    {
        title: "Documents",
        description: "Store and share project files securely in one central location.",
        tag: "Storage"
    },
    {
        title: "Analytics",
        description: "Track performance metrics and gain insights into your business.",
        tag: "Insights"
    },
    {
        title: "More Features",
        description: "More Features to come soon in the following updates.",
        tag: "soon"
    }
];

export const MaintenancePage = () => {
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);

    const handleSubscribe = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            const { error } = await supabase
                .from('waitlist')
                .insert([{ email, name }]);

            if (error) throw error;

            setShowSuccess(true);
            setEmail('');
            setName('');
        } catch (error) {
            toast.error('Already subscribed or invalid email!');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-white">
            <Toaster position="top-center" />

            {/* Background Pattern */}
            <div className="fixed top-0 right-0 w-1/2 h-full">
                <div className="w-full h-full bg-indigo-600 opacity-5 transform rotate-45 scale-150" />
            </div>

            <div className="relative w-full">
                {/* Header */}
                <header className="py-4 px-8">
                    <div className="max-w-6xl mx-auto">
                        <div className="flex items-center h-12">
                            <Diamond className="h-8 w-8 text-[#5930d2]" strokeWidth={3.5} />
                            <img
                                src={Logo}
                                alt="LanceGO"
                                className="h-[140px] object-contain -ml-1"
                            />
                        </div>
                    </div>
                </header>

                <main className="flex-1 px-0 lg:px-8 py-6">
                    <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-12">
                        {/* Left Column */}
                        <div className="flex flex-col justify-center lg:col-span-2 px-4 lg:px-0">
                            <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-indigo-50 text-indigo-600 text-sm font-medium mb-4">
                                <Rocket className="w-4 h-4 mr-2" />
                                Launching Soon
                            </div>

                            <h1 className="text-4xl font-bold text-gray-900 mb-4 leading-tight">
                                The Ultimate Platform for
                                <span className="block text-[#5930d2]">
                                    Modern Freelancers
                                </span>
                            </h1>

                            <p className="text-lg text-gray-600 mb-8">
                                Hey there! 👋 I'm building LanceGo because, like you, I've struggled with juggling multiple tools as a freelancer.
                                This is the all-in-one platform I wish I had when I started - designed by a freelancer, for freelancers.
                            </p>

                            <form onSubmit={handleSubscribe} className="space-y-3 max-w-md mb-8">
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="Name"
                                    className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none text-sm"
                                    required
                                />
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Email"
                                    className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none text-sm"
                                    required
                                />
                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="w-full py-2 rounded-lg bg-[#5930d2] text-white text-sm font-medium hover:bg-indigo-700 transition-colors duration-200 disabled:opacity-70"
                                >
                                    {isLoading ? 'Adding...' : 'Join the Waitlist'}
                                </button>

                                {showSuccess && (
                                    <div className="bg-green-50 border border-green-100 rounded-lg p-4 mt-4">
                                        <p className="text-green-800 text-sm">
                                            <span className="font-medium">Awesome! 🎉</span> Thanks for joining the waitlist!
                                        </p>
                                        <p className="text-green-700 text-xs mt-1">
                                            I'm super excited to have you onboard! I'll personally reach out when we're ready to launch.
                                            Get ready to transform your freelancing journey! 🚀✨
                                        </p>
                                    </div>
                                )}

                                <p className="text-xs text-gray-500">
                                    ⚡️ Early supporters get 6 months free access when we launch!
                                </p>

                                <p className="text-sm font-medium text-indigo-600 bg-indigo-50 px-3 py-2 rounded-lg">
                                    Join 50+ freelancers already waiting to simplify their workflow
                                </p>
                            </form>

                            <div className="text-sm text-gray-500 mb-6">
                                <p>
                                    Built with ❤️ by an indie developer. Questions? Email me at{' '}
                                    <a
                                        href="mailto:pramodhworkspace@gmail.com"
                                        className="text-indigo-600 hover:text-indigo-700"
                                    >
                                        pramodhworkspace@gmail.com
                                    </a>
                                </p>
                            </div>
                        </div>

                        {/* Right Column - Features */}
                        <div className="bg-white rounded-none lg:rounded-2xl shadow-sm border-x-0 border-y lg:border px-4 lg:px-6 py-6 flex flex-col lg:col-span-3">
                            <h2 className="text-base font-semibold text-gray-900 mb-4">
                                Everything you need in one place
                            </h2>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {features.map((feature, index) => (
                                    <div
                                        key={index}
                                        className="group p-5 rounded-xl hover:bg-gray-50 transition-colors duration-200 border border-gray-100 w-full"
                                    >
                                        <div className="flex flex-col h-full">
                                            <div className="flex items-center space-x-2 mb-2">
                                                <h3 className="font-medium text-base text-gray-900">
                                                    {feature.title}
                                                </h3>
                                                <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600 group-hover:bg-indigo-100 transition-colors duration-200">
                                                    {feature.tag}
                                                </span>
                                            </div>
                                            <p className="text-sm text-gray-600 leading-relaxed">
                                                {feature.description}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
};